import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import { AnswerOLD, OLDLessonConcept, AssessmentOLD } from "../model/content";
import { Grade, Result, Subject } from "../model/subject"
import { QuizResult, QuizSubject } from "../model/exercises/quiz"
import { EnsembleTuningResult, ensembleTuningSubject } from "../model/exercises/ensemble-tuning"

/**
 * Provides statistics over the completed tests (questions/exercises).
 * For each subject, the last 100 test results are saved.
 */
@Injectable()
export class StatisticsService {

	readonly resultsCount = 100

	constructor(private storage: Storage) {
	}

	/**
	 * Gets the overall grade for the given subject.
	 * If less than [resultsCount] tests were completed, the
	 * missing ones are counted as wrong.
	 */
	async getGrade<R>(subject: Subject<any, R>): Promise<Grade> {
		const results: R[] = await this.getResults(subject)
		return subject.grade(results)
	}

	/**
	 * Gets the overall grade (0..1) for each category of the given quiz.
	 */
	async getGradeByCategory(subject: QuizSubject<any>): Promise<{category, grade: Grade, answersCount: number}[]> {
		const results: QuizResult[] = await this.getResults(subject)
		return subject.categories.map(category => {
			const filtered = results.filter(r => r.category == category)
			return {
				category,
				grade: subject.grade(filtered),
				answersCount: filtered.length
			}
		})
	}

	/**
	 * Gets the count for each tuning of the ensemble tuning subject.
	 */
	async getTuningsCount(): Promise<{[mistuning: string]: number}> {
		const results: EnsembleTuningResult[] = await this.getResults(ensembleTuningSubject)
		let ret = {}
		ensembleTuningSubject.tunings.forEach(tuning =>
				ret[""+tuning] = results.filter(r => r.tuning == tuning).length)
		return ret
	}

	/**
	 * Call this method when a test or several tests were completed.
	 * The results are saved for the statistics for the given subject.
	 */
	async reportResults<R>(subject: Subject<any, R>, results: R[]) {
		const oldResults: R[] = await this.getResults(subject)
		results = results.concat(oldResults)
		results = results.slice(0, this.resultsCount)
		this.storage.set(this.getKey(subject), results)
	}

	/**
	 * Gets the saved results for the given subject.
	 */
	private async getResults<R>(subject: Subject<any, R>): Promise<R[]> {
		try {
			const results = await this.storage.get(this.getKey(subject))
			if (results == null)
				return []
			return results
		}
		catch (ex) {
			return []
		}
	}

	/**
	 * Gets the storage key for the given subject.
	 */
	private getKey(subject: Subject<any, any>): string {
		return "statistics-" + subject.id
	}

}



// WEBPACK FOOTER //
// ./src/service/statistics.service.ts